import type {
  EventProgramItem,
  ProgramItemKind,
} from "@/types/EventProgramItem";
import { defaultTrackId } from "@/lib/utils/programmeTracks";

/**
 * Shared, component-free helpers of the programme manager — lifted out of
 * the ProgramTab monolith (W4/F2) so the timeline (F4) and the form (F3)
 * consume the exact same time math and form-state shapes. Pure module: no
 * React, no components.
 */

/** Tolerant kind read: absent = SESSION (every pre-cut row). ALWAYS go
 *  through this — never read `item.kind` raw. */
export const itemKind = (
  item: Pick<EventProgramItem, "kind">,
): ProgramItemKind => item.kind ?? "SESSION";

/** Tolerant "Alle Tracks" read: ONLY an explicit true spans (absent/garbage
 *  = false). Spanning items belong to every track — track visibility filters
 *  and bake exclusions must never hide them. */
export const isAllTracks = (
  item: Pick<EventProgramItem, "spansAllTracks">,
): boolean => item.spansAllTracks === true;

/** "HH:mm" of an ISO instant in the event's zone (invalid zone → local). */
export const formatItemTime = (
  iso: string | undefined,
  timeZone: string | undefined,
  locale: string,
): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const opts: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
  };
  try {
    return new Intl.DateTimeFormat(locale, { ...opts, timeZone }).format(date);
  } catch {
    return new Intl.DateTimeFormat(locale, opts).format(date);
  }
};

/** Whole minutes between two ISO instants split into {hours, minutes} for the
 *  schedule readout ("Dauer: 1 Std 30 Min") — numeric compare (mixed ISO
 *  formats), null for absent/garbage/non-positive spans. Same rounding as the
 *  agenda summary in ProgramTab. */
export const durationHM = (
  startIso: string | undefined,
  endIso: string | undefined,
): { hours: number; minutes: number; totalMinutes: number } | null => {
  if (!startIso || !endIso) return null;
  const delta = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(delta) || delta <= 0) return null;
  const totalMinutes = Math.round(delta / 60_000);
  if (totalMinutes <= 0) return null;
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    totalMinutes,
  };
};

/** End-time synthesis for the BIND PUT (R1 + audit C2b): a bound block needs
 *  a STRICT start < end (the window is mirrored onto the container, whose own
 *  validation rejects equal instants — the BE 400s otherwise). Returns the
 *  +60min end to send when the stored end is absent OR not after the start
 *  (zero-length marker sessions, garbage strings); undefined when the stored
 *  window is already valid — or the start itself is unusable (the PUT then
 *  travels without an end and the server's own validation answers). */
export const synthesizedBindEndTime = (
  startIso: string | undefined,
  endIso: string | undefined,
): string | undefined => {
  if (!startIso) return undefined;
  const startMs = new Date(startIso).getTime();
  if (Number.isNaN(startMs)) return undefined;
  if (endIso) {
    const endMs = new Date(endIso).getTime();
    if (endMs > startMs) return undefined;
  }
  return new Date(startMs + 60 * 60 * 1000).toISOString();
};

export type ProgramFormState = {
  title: string;
  description: string;
  /** ISO instant ("" = unset) — edited via the tz-aware DatePicker field. */
  start: string;
  end: string;
  location: string;
  /** STORED seating-room id link ("" = none). Never edited directly — the
   *  UI edits `location` text; the id is re-resolved at save time (name
   *  match wins, untouched-location keeps this stored id across renames). */
  roomId: string;
  /** Seat limit as the raw input string; "" = unlimited (no capacity). */
  capacity: string;
  speakerIds: string[];
  sponsorIds: string[];
  /** "" = no track (an orphan, or a flat event without tracks). */
  programmeId: string;
  /** Break/structure toggle (emergent-kind pivot): the ONLY explicit kind
   *  input left in the form. The display kind is DERIVED — offeringId set →
   *  OFFERING, else isBreak → BREAK, else SESSION (derivedFormKind). */
  isBreak: boolean;
  /** The block's 1:1 slotting offering CONTAINER ("" = none). Minted
   *  silently by the FIRST Terminart created in the form; the container
   *  holds n Terminarten. A set container makes the item BOOKABLE (the
   *  server derives kind OFFERING from it). */
  offeringId: string;
  /** SESSION only: check-in requires a session registration. Meaningful only
   *  while a capacity is set — clearing capacity clears this too. */
  requiresRegistration: boolean;
  /** "Alle Tracks" pick in the track select — mutually exclusive with
   *  programmeId (picking a concrete track clears the flag and vice versa). */
  spansAllTracks: boolean;
};

/** Display kind of the FORM state (emergent-kind pivot): content decides —
 *  a bound container makes it OFFERING, the toggle makes it BREAK, everything
 *  else is a plain SESSION. Mirrors the server's derive rule. */
export const derivedFormKind = (
  form: Pick<ProgramFormState, "offeringId" | "isBreak">,
): ProgramItemKind =>
  form.offeringId ? "OFFERING" : form.isBreak ? "BREAK" : "SESSION";

export const emptyForm = (
  programmeId?: string | null,
  /** ISO prefill for the start field — the previous item's end time, so a
   *  back-to-back agenda types itself. End follows at +1h. */
  startPrefill?: string,
): ProgramFormState => ({
  title: "",
  description: "",
  start: startPrefill ?? "",
  end: startPrefill
    ? new Date(new Date(startPrefill).getTime() + 60 * 60 * 1000).toISOString()
    : "",
  location: "",
  roomId: "",
  capacity: "",
  speakerIds: [],
  sponsorIds: [],
  programmeId: programmeId ?? "",
  isBreak: false,
  offeringId: "",
  requiresRegistration: false,
  spansAllTracks: false,
});

export const toForm = (item: EventProgramItem): ProgramFormState => ({
  title: item.title ?? "",
  description: item.description ?? "",
  start: item.startTime ?? "",
  end: item.endTime ?? "",
  location: item.location ?? "",
  roomId: item.roomId ?? "",
  capacity: item.capacity != null ? String(item.capacity) : "",
  speakerIds: item.speakerIds ?? [],
  sponsorIds: item.sponsorIds ?? [],
  programmeId: item.programmeId ?? "",
  isBreak: itemKind(item) === "BREAK",
  // Hygiene row (server downgraded the kind on offering delete, the id
  // lingered): an EXPLICIT "SESSION" next to an offeringId means the
  // container is gone — the form must offer make-bookable, never "manage" a
  // dangling container (writes there 404). Keyed on the explicit value, NOT
  // on itemKind(): an ABSENT kind is a pre-cut row, where a present
  // offeringId is a real binding and dropping it would mint a second
  // container on the next save.
  offeringId: item.kind === "SESSION" ? "" : (item.offeringId ?? ""),
  requiresRegistration: item.requiresRegistration ?? false,
  spansAllTracks: isAllTracks(item),
});

/** "" → undefined (unlimited); otherwise the parsed integer, NaN when the
 *  input is not a whole number ≥ 1 (validation error). */
export const parseCapacityInput = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : Number.NaN;
};

/** Sentinel for the "no programme" pick in the programme OptionInput. */
export const UNASSIGNED = "__unassigned__";

/** Sentinel for the "Alle Tracks (überall sichtbar)" pick — maps onto
 *  spansAllTracks=true with programmeId cleared. */
export const ALL_TRACKS = "__allTracks__";

/** A seating room offered as a location suggestion, with its REAL seat count
 *  (sum of its furniture's seats) for the one-click capacity fill. `roomId`
 *  is the persisted id-link (rename-immune, unlike the display name). */
export type RoomSuggestion = { roomId: string; name: string; seatCount: number };

/**
 * Resolves which stored room link a saved form should carry. Name match on
 * the location text wins (the user just named a room); an untouched location
 * keeps the stored id even when the room was renamed underneath (the text
 * merely went stale); any other text is a real move away from the room.
 * Only meaningful when the rooms actually loaded — callers must not clear
 * a stored link off a failed/403 seating fetch.
 */
export const resolveRoomLink = (
  rooms: readonly RoomSuggestion[],
  location: string,
  storedRoomId: string,
  storedLocation: string,
): string | undefined => {
  const trimmed = location.trim();
  // UNTOUCHED location + valid stored id wins outright (audit FE-F4): the
  // user expressed no new intent, so a rename collision (another room now
  // carrying this item's old location text) must not silently relink.
  if (
    storedRoomId &&
    storedLocation.trim() === trimmed &&
    rooms.some((room) => room.roomId === storedRoomId)
  ) {
    return storedRoomId;
  }
  const byName = trimmed
    ? rooms.find(
        (room) => room.name.trim().toLowerCase() === trimmed.toLowerCase(),
      )
    : undefined;
  return byName?.roomId;
};

/** Catalogue context a save needs to guard its id-links against staleness.
 *  Structural (not the full EventProgramme type) — callers pass whatever
 *  loaded list they hold. */
export type ProgramSaveContext = {
  programmes: readonly { programmeId?: string }[];
  seatingRooms: readonly RoomSuggestion[];
  /** POSITIVE seating load (query success) — only then is the room link
   *  managed at all (neither sent nor cleared off a failed/403 fetch). */
  seatingLoaded: boolean;
  /**
   * Per-event plugin gates. A field whose plugin is OFF is hidden in the form
   * AND left off the wire entirely — the D5 rule that already governs a bound
   * block's capacity: a value the user cannot see must never be sent, and
   * never cleared. Absent (the builder mount, which has no event document)
   * means "on", exactly like `slottingAvailable`.
   */
  speakersEnabled?: boolean;
  sponsorsEnabled?: boolean;
};

export type ProgramSavePlan = {
  payload: Partial<EventProgramItem>;
  /** Explicit $unset flags for the update PUT — all false on create. */
  clearEndTime: boolean;
  clearCapacity: boolean;
  clearProgrammeId: boolean;
  clearRoomId: boolean;
};

/**
 * The save payload + clear flags of the programme item form — lifted out of
 * ProgramTab's handleSave (W4/F5) so the hub page's timeline modal and
 * ProgramTab share ONE wire-shape implementation. Pure; the caller owns the
 * mutation call (create adds sortIndex, update passes the flags).
 *
 * Emergent-kind wire rule (pivot 3, H7): the payload NEVER echoes a kind for
 * the server to store — kind is server-derived from content. Exactly two
 * exceptions travel:
 *   - kind:"BREAK" while the break toggle is ON (the toggle IS the input);
 *   - kind:"SESSION" as the explicit toggle-OFF of a STORED break.
 * offeringId travels whenever the form holds one (the server derives
 * OFFERING from it and 400s any payload carrying kind together with
 * offeringId — symmetric mismatch rule).
 */
export const buildSavePayload = (
  form: ProgramFormState,
  /** undefined on CREATE; the STORED item on edit. */
  existing: EventProgramItem | undefined,
  isCreate: boolean,
  ctx: ProgramSaveContext,
): ProgramSavePlan => {
  // Display kind DERIVED from content (offeringId → OFFERING beats the
  // toggle) — every per-kind gate below keys on this, never on a stored or
  // echoed kind.
  const kind = derivedFormKind(form);
  const storedKind = itemKind(existing ?? {});
  // The ONLY kind that ever travels: the break toggle (see doc above).
  // Never combined with offeringId — derivation makes that impossible
  // (offeringId forces kind OFFERING, which is never sent).
  const wireKind: ProgramItemKind | undefined =
    kind === "BREAK"
      ? "BREAK"
      : !isCreate && storedKind === "BREAK" && kind === "SESSION"
        ? "SESSION"
        : undefined;
  // Form validation guarantees this is undefined ("" = unlimited) or ≥ 1.
  // Capacity only travels on a plain SESSION (BREAK: forbidden — the server
  // auto-clears on the break toggle, the clearCapacity flag below is the
  // belt-and-braces twin; OFFERING: hidden per D5 — grandfathered values
  // must stay untouched, never sent/cleared).
  const capacity =
    kind === "SESSION" ? parseCapacityInput(form.capacity) : undefined;
  // Room link is only managed when the rooms actually loaded — a failed/403
  // seating fetch must neither send a stale id (verifyRoomId would 400 the
  // whole save) nor clear the stored link.
  const roomId =
    kind !== "BREAK" && ctx.seatingLoaded
      ? resolveRoomLink(
          ctx.seatingRooms,
          form.location,
          form.roomId,
          existing?.location ?? "",
        )
      : undefined;
  // Container link: sent whenever the form holds one. NO staleness guard
  // (unlike programmeId): the id is minted by the form's own Terminart flow,
  // never picked from a possibly-stale list, and the backend verifies
  // liveness anyway (400 invalidEventProgramItemOfferingId).
  const offeringId = form.offeringId || undefined;
  /** The form's track is one of the loaded tracks (a stale id is not). */
  const liveTrack =
    !!form.programmeId &&
    ctx.programmes.some((p) => p.programmeId === form.programmeId);
  /** R3 for a pause: a NEW break without a live track in an event that has
   *  tracks spans all of them (a session gets the first track instead). */
  const breakSpansByDefault =
    isCreate &&
    kind === "BREAK" &&
    !form.spansAllTracks &&
    !liveTrack &&
    ctx.programmes.some((p) => !!p.programmeId);
  const payload: Partial<EventProgramItem> = {
    ...(wireKind ? { kind: wireKind } : {}),
    title: form.title.trim(),
    // "" (not undefined) — undefined is dropped from the PUT and the value
    // could never be cleared (audit finding).
    description: form.description.trim(),
    startTime: form.start,
    endTime: form.end || undefined,
    // BREAK is minimal (title/times/description + its track, below) —
    // everything else stays off the wire entirely (the validator forbids
    // most of it anyway).
    ...(kind !== "BREAK"
      ? {
          location: form.location.trim(),
          ...(roomId ? { roomId } : {}),
          // Plugin-gated links (D5 shape): with the plugin off the picker is
          // not on screen, so the form state is not the user's answer — sending
          // it would clear a stored list nobody could see, and the field stays
          // off the wire until the plugin comes back.
          ...(ctx.speakersEnabled === false
            ? {}
            : { speakerIds: form.speakerIds }),
          ...(ctx.sponsorsEnabled === false
            ? {}
            : { sponsorIds: form.sponsorIds }),
        }
      : {}),
    // TRACK — every kind, BREAK included (Welle C: a pause sits in a track
    // or spans "All tracks" like any item; the backend always allowed it).
    // Guard: a stale programmeId (programme deleted elsewhere, cache not yet
    // refetched) would 400 the whole save via verifyProgrammeId — only send
    // ids from the loaded list; a CREATE then falls back to the default
    // (first) track, so an event with tracks never gains a track-less item
    // (R3's FE half — the form prefills the same default). NEVER together
    // with spansAllTracks (server-invalid combination). A BREAK create
    // without a live track spans "All tracks" instead — the server's own R3
    // default for a pause (a pause dropped into one arbitrary lane is what
    // imports and the AI produced before), so both sides agree.
    ...(form.spansAllTracks || breakSpansByDefault
      ? {}
      : (() => {
          const track = liveTrack
            ? form.programmeId
            : isCreate
              ? defaultTrackId(ctx.programmes)
              : undefined;
          return track ? { programmeId: track } : {};
        })()),
    // "Alle Tracks" flag — mirror of requiresRegistration's wire rule: sent
    // when TRUE, sent as explicit false ONLY to clear a stored true (absent
    // would not clear), omitted otherwise so pre-cut backends never see the
    // field.
    ...(form.spansAllTracks || breakSpansByDefault
      ? { spansAllTracks: true }
      : isAllTracks(existing ?? {})
        ? { spansAllTracks: false }
        : {}),
    ...(capacity !== undefined ? { capacity } : {}),
    ...(offeringId ? { offeringId } : {}),
    // SESSION only, and only meaningful with a capacity: clearing the seat
    // limit clears the toggle too (explicit false — absent wouldn't clear).
    // BIND (derived OFFERING over a stored requiresRegistration): the server
    // auto-clears on the flip — the explicit false here is the form path's
    // belt-and-braces clear (TRUE next to offeringId would be rejected).
    // NOT plugin-gated: the toggle is a door rule ("only guests registered for
    // this session can check in"), but the seat limit it hangs on is website
    // -facing (the agenda renders a capacity badge) and organisers configure
    // both long before check-in is switched on. Hiding it would also make it
    // the one hide-but-keep field whose plugin differs from the surface it
    // lives on.
    ...(kind === "SESSION"
      ? {
          requiresRegistration:
            capacity !== undefined && form.requiresRegistration,
        }
      : kind === "OFFERING" &&
          !isCreate &&
          (existing?.requiresRegistration ?? false)
        ? { requiresRegistration: false }
        : {}),
  };
  // A blanked end-time/seat-limit is an explicit clear — the wire can't
  // tell absent from null, so each travels as a flag (backend $unset).
  // Same for dropping an item's track (the "All tracks" pick — BREAK too).
  // A DERIVED OFFERING never clears its end (D4 — the form validates it in)
  // and never clears offeringId (clearOfferingId is rejected on OFFERING
  // server-side; the only unbind is the server's offering-delete downgrade).
  const clearEndTime =
    !isCreate && kind !== "OFFERING" && Boolean(existing?.endTime) && !form.end;
  // Break toggle clears a stored seat limit via the $unset flag (belt-and-
  // braces twin of the server's auto-clear — $set 0 would be illegal);
  // a bound block (derived OFFERING) keeps grandfathered values (D5).
  const clearCapacity =
    !isCreate &&
    existing?.capacity != null &&
    (kind === "BREAK" || (kind === "SESSION" && capacity === undefined));
  const clearProgrammeId =
    !isCreate && Boolean(existing?.programmeId) && !form.programmeId;
  // Only clear off POSITIVE knowledge (rooms loaded, link resolved
  // away) — never off a failed seating fetch.
  const clearRoomId =
    !isCreate &&
    kind !== "BREAK" &&
    ctx.seatingLoaded &&
    Boolean(existing?.roomId) &&
    !roomId;
  return { payload, clearEndTime, clearCapacity, clearProgrammeId, clearRoomId };
};

/**
 * The duplicate payload of a programme item — lifted out of ProgramTab's
 * handleDuplicate (phase 2) so the hub page's edit-modal Duplicate and the
 * list rows share ONE semantics implementation. Pure; the caller owns the
 * create mutation (and the pending-guard against double-clicks).
 */
export const buildDuplicateItem = (
  item: EventProgramItem,
  ctx: {
    /** Localized copy title (t("copySuffix", { title })) — passed in so this
     *  module stays component-free. */
    copyTitle: string;
    /** Append slot (nextProgramSortIndex over the FULL list). */
    nextSortIndex: number;
    programmes: readonly { programmeId?: string }[];
    seatingRooms: readonly RoomSuggestion[];
    /** POSITIVE seating load — roomId is only stripped off real knowledge. */
    seatingLoaded: boolean;
  },
): EventProgramItem => {
  // Strip identity/server fields; the copy lands at the end of the agenda.
  const {
    programItemId: _programItemId,
    eventId: _eventId,
    createdAt: _createdAt,
    ...rest
  } = item;
  const copy: EventProgramItem = {
    ...rest,
    title: ctx.copyTitle,
    sortIndex: ctx.nextSortIndex,
  };
  // Duplicates NEVER carry a binding: containers are STRICTLY 1:1
  // (partial-unique {eventId,offeringId}) — a copied binding would 409
  // offeringAlreadyScheduled, a dangling one 400
  // invalidEventProgramItemOfferingId, and kind next to offeringId 400s the
  // mismatch rule. Key the strip on offeringId PRESENCE, not stored kind —
  // a hygiene row (stored SESSION with a stale offeringId) must strip too.
  // The copy lands as a plain SESSION with fresh Terminarten to create
  // (the server derives kind from the stripped content; callers surface
  // the duplicateLosesBooking hint). BREAK copies without an offeringId
  // keep their explicit kind:"BREAK" (the toggle's wire form — legal
  // without offeringId).
  if (copy.offeringId) {
    delete copy.kind;
    delete copy.offeringId;
  }
  // spansAllTracks copies (a plenary's duplicate is plenary too) — but the
  // programmeId+flag combination is server-invalid, so a corrupt source row
  // carrying both must not 400 the create: the flag wins.
  if (isAllTracks(copy)) {
    delete copy.programmeId;
  } else if (
    !copy.programmeId ||
    !ctx.programmes.some((p) => p.programmeId === copy.programmeId)
  ) {
    // Same stale-programmeId guard as save (a programme deleted elsewhere
    // would 400 the create via verifyProgrammeId) — and, like save, the copy
    // of an orphan or of a gone track lands in the DEFAULT track (Welle C:
    // never track-less while tracks exist; none for a flat event).
    const fallback = defaultTrackId(ctx.programmes);
    if (fallback) copy.programmeId = fallback;
    else delete copy.programmeId;
  }
  // Room-link twin: only strip on POSITIVE knowledge (rooms loaded, id
  // gone) — a failed/403 seating fetch says nothing about validity, and
  // the backend $unsets deleted rooms anyway.
  if (
    copy.roomId &&
    ctx.seatingLoaded &&
    !ctx.seatingRooms.some((room) => room.roomId === copy.roomId)
  ) {
    delete copy.roomId;
  }
  return copy;
};

/** Append slot for create/duplicate: max sortIndex + 1, NOT list length —
 *  deletions leave holes and length would mint a tie. Since the ordering cut
 *  `sortIndex` is only a TIEBREAK (the display order is `startTime`), so a tie
 *  no longer strands anything; it just makes two same-instant blocks fall
 *  through to `createdAt`/id. The server applies the same max+1 rule when the
 *  payload omits the field — these two must not drift apart. */
export const nextProgramSortIndex = (
  items: readonly EventProgramItem[],
): number =>
  items.reduce((max, i) => Math.max(max, (i.sortIndex ?? -1) + 1), 0);

/** New items start where the agenda currently ends (latest end — or start,
 *  for open-ended items), so a back-to-back agenda needs no time typing.
 *  Numeric compare (mixed ISO formats) + skips unparsable garbage — a bad
 *  stored string must not crash the Add form via emptyForm's toISOString(). */
export const programStartPrefill = (
  items: readonly EventProgramItem[],
): string | undefined => {
  let latest = "";
  let latestMs = Number.NEGATIVE_INFINITY;
  for (const item of items) {
    const end = item.endTime || item.startTime || "";
    const endMs = end ? new Date(end).getTime() : Number.NaN;
    if (!Number.isNaN(endMs) && endMs > latestMs) {
      latestMs = endMs;
      latest = end;
    }
  }
  return latest || undefined;
};

/** UTC offset (ms) of IANA `zone` at instant `atMs`. Throws on bad zone —
 *  callers fall back to a plain UTC shift. */
export const tzOffsetMs = (atMs: number, zone: string): number => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(atMs));
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  const wallAsUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    // hour12:false can yield "24" at midnight in some engines.
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return wallAsUtc - atMs;
};

/** Shift an ISO instant by `deltaMs` of WALL-CLOCK time in `zone`: "+7 days"
 *  across a DST boundary keeps 09:00 sessions at 09:00 local (a plain UTC
 *  add would silently move the whole agenda by an hour). */
export const shiftWallClock = (
  iso: string,
  deltaMs: number,
  zone?: string,
): string => {
  const t0 = new Date(iso).getTime();
  const naive = t0 + deltaMs;
  if (!zone) return new Date(naive).toISOString();
  try {
    const corrected = naive - (tzOffsetMs(naive, zone) - tzOffsetMs(t0, zone));
    return new Date(corrected).toISOString();
  } catch {
    return new Date(naive).toISOString();
  }
};
